import React, { useState, useEffect, ChangeEvent } from 'react';
import { useHistory } from 'react-router-dom';

import Loader from '../../../elements/Loader';
import AdvancedSelect from '../../../elements/formElements/AdvancedSelect';
import FormFileGroup from '../../../elements/formElements/FormFileGroup';
import FormGroupDatetime from '../../../elements/formElements/FormGroupDatetime';
import FormGroupNumber from '../../../elements/formElements/FormGroupNumber';
import FormGroupText from '../../../elements/formElements/FormGroupText';
import FormGroupTextEditor from '../../../elements/formElements/FormGroupTextEditor';
import SubmitButton from '../../../elements/formElements/SubmitButton';
import axios from '../../../services/MyAxios';
import { CasinoDepositBonus } from '../../Models/CasinoDepositBonus';
import {
    API_CASINO_DEPOSIT_BONUSES,
    API_CASINO_DEPOSIT_BONUSES_CREATE,
    API_CASINO_UPLOAD_PNG_IMAGE,
} from '../Helpers/CrmApiUrls';
import { CASINO_DEPOSIT_BONUSES } from '../Helpers/CrmUrls';

import SweetAlert from 'crm/elements/alerts/SweetAlert';
import FormGroupTextArea from "../../../elements/formElements/FormGroupTextArea";

function DepositBonusCreate() {
    const history = useHistory();
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [depositBonus, setDepositBonus] = useState<CasinoDepositBonus>({
        id: 0,
        name: '',
        casino_deposit_bonus_page_id: 0,
        currency_code: '',
        image_path: '',
        description: '',
        min_deposit: 1,
        max_deposit: 1,
        bonus_percentage: 1.0,
        wagering_requirement: 1,
        count_of_days_to_fill_requirements_by_user: 1,
        available_from: '',
        available_to: '',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    const [depositBonusPages, setDepositBonusPages] = useState({});

    useEffect(() => {
        axios
            .get<CasinoDepositBonus>(`${API_CASINO_DEPOSIT_BONUSES}/get-pages-in-primary-language`)
            .then((response) => {
                setDepositBonusPages(response.data);
            });
        setIsLoading(false);
    }, []);

    const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        setDepositBonus({ ...depositBonus, [event.target.name]: event.target.value });
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        axios
            .post(`${API_CASINO_DEPOSIT_BONUSES_CREATE}`, depositBonus)
            .then(() => {
                SweetAlert.success('Bonus page created correctly').then(() => {
                    history.push({ pathname: CASINO_DEPOSIT_BONUSES });
                });
            })
            .catch((error) => {
                if (!error.response) {
                    setErrors(error.message);
                } else if (error.response.status !== 500) {
                    setErrors(error.response.data.errors);
                } else {
                    SweetAlert.error('Server error');
                }
            });
    };

    const uploadImage = (image: File) => {
        axios
            .post(
                API_CASINO_UPLOAD_PNG_IMAGE,
                { image, dir: 'deposit-bonus' },
                {
                    headers: {
                        Accept: 'application/json',
                        'content-type': 'multipart/form-data',
                    },
                }
            )
            .then((response) => {
                const url = response.data.url;
                setDepositBonus({
                    ...depositBonus,
                    image_path: url,
                });
            })
            .catch(() => {
                SweetAlert.error('Server error');
            });
    };

    const getContent = () => {
        return (
            <div className="card">
                <div className="card-body">
                    <form className="form-flex">
                        <FormGroupText
                            label="Name"
                            name="name"
                            handleChange={handleChange}
                            value={depositBonus.name}
                            error={errors.name ?? undefined}
                            inputClasses="w-80"
                        />

                        <AdvancedSelect
                            label="Deposit bonus page"
                            field="casino_deposit_bonus_page_id"
                            value={depositBonus.casino_deposit_bonus_page_id}
                            handleChange={handleChange}
                            error={errors.casino_deposit_bonus_page_id ?? undefined}
                            options={depositBonusPages}
                            optionAccessor="slug"
                            optionValueAccessor="id"
                            inputClasses="w-80"
                        />
                        <FormFileGroup
                            label="Image .png"
                            field="image_path"
                            value={depositBonus.image_path}
                            handleChange={(event: ChangeEvent<HTMLInputElement>) => {
                                uploadImage(event.target.files[0]);
                            }}
                            acceptExtensions=".png"
                            error={errors.image_path ?? undefined}
                        />
                        <FormGroupTextArea
                            name="description"
                            label="Description"
                            handleChange={handleChange}
                            value={depositBonus.description}
                            error={errors.description ?? undefined}
                        />

                        <div>
                            <h5 className="card-title">Rules</h5>
                            <FormGroupNumber
                                label="Min deposit"
                                name="min_deposit"
                                value={depositBonus.min_deposit}
                                error={errors.min_deposit ?? undefined}
                                handleChange={handleChange}
                                required
                                unit={process.env.MIX_CURRENCY}
                                inputClasses="w-25"
                                minValue={1}
                            />

                            <FormGroupNumber
                                label="Max deposit"
                                name="max_deposit"
                                value={depositBonus.max_deposit}
                                error={errors.max_deposit ?? undefined}
                                handleChange={handleChange}
                                required
                                inputClasses="w-25"
                                unit={process.env.MIX_CURRENCY}
                                minValue={1}
                            />
                            <FormGroupNumber
                                label="Wagering requirement"
                                name="wagering_requirement"
                                value={depositBonus.wagering_requirement}
                                error={errors.wagering_requirement ?? undefined}
                                handleChange={handleChange}
                                required
                                inputClasses="w-25"
                                unit="X"
                                minValue={1}
                            />
                            <FormGroupNumber
                                label="Bonus percentage"
                                name="bonus_percentage"
                                value={depositBonus.bonus_percentage}
                                error={errors.bonus_percentage ?? undefined}
                                handleChange={handleChange}
                                required
                                inputClasses="w-25"
                                unit="%"
                                minValue={1}
                            />
                            <FormGroupNumber
                                label="Count of days to fill requirements by user"
                                name="count_of_days_to_fill_requirements_by_user"
                                value={depositBonus.count_of_days_to_fill_requirements_by_user}
                                error={errors.count_of_days_to_fill_requirements_by_user ?? undefined}
                                handleChange={handleChange}
                                required
                                inputClasses="w-25"
                                unit="day(s)"
                                minValue={1}
                            />

                            <FormGroupDatetime
                                label="Available to activate from"
                                name="available_from"
                                error={errors.available_from}
                                handleChange={handleChange}
                                value={depositBonus.available_from}
                                required
                            />

                            <FormGroupDatetime
                                label="Available to activate to"
                                name="available_to"
                                error={errors.available_to}
                                handleChange={handleChange}
                                value={depositBonus.available_to}
                                required
                            />
                        </div>
                        <SubmitButton label="Save" onClick={handleSubmit} />
                    </form>
                </div>
            </div>
        );
    };

    return <Loader loading={isLoading} content={getContent} />;
}

export default DepositBonusCreate;
